/*
	Shared component for editing matter permissions.
	Used by both EditMatterPermissions (single matter) and BulkMatterPermissions (multiple matters).
*/

import React from "react";
import PropTypes from "prop-types";
import { LabeledDropDownSelect } from "./DropDownSelect";
import UsersDropDownSelect from "./UsersDropDownSelect";
import { components } from "react-select";
import { Accordion, HoverCard, Text } from "@mantine/core";

import {
	allowListDropdownMap,
	findAdminGroups,
	groupListDropdownMap,
	matterPermissionGlobalSettingLookup,
	matterPermissionGlobalSettingOptions,
} from "../utilities/utilities";

// Label configurations for different contexts
const LABEL_CONFIGS = {
	SINGLE_MATTER: {
		main: "This Matter is Accessible to",
		user: "Users with Access to this Matter",
		group: "Groups with Access to this Matter",
	},
	MULTIPLE_MATTERS: {
		main: "Make These Matters Accessible to",
		user: "Users with Access to these Matters",
		group: "Groups with Access to these Matters",
	},
	DEFAULT_MATTER_PERMISSIONS: {
		main: "By Default, New Matters Will Be Accessible To",
		user: "Users with Access to New Matters by Default",
		group: "Groups with Access to New Matters by Default",
	},
};

// Custom Option component with hover tooltip for group members
const GroupOptionWithTooltip = (props) => {
	const memberNames = props.data.memberNames || [];
	const hasMemberNames = memberNames.length > 0;
	const displayLimit = 20;
	const displayNames =
		memberNames.length > displayLimit
			? memberNames.slice(0, displayLimit)
			: memberNames;
	const remainingCount = memberNames.length - displayLimit;

	return (
		<HoverCard
			position="right-start"
			openDelay={0}
			closeDelay={0}
			withinPortal
			zIndex={9999}
		>
			<HoverCard.Target>
				<div>
					<components.Option {...props} />
				</div>
			</HoverCard.Target>
			<HoverCard.Dropdown
				style={{
					padding: "8px 12px",
					maxWidth: 400,
				}}
			>
				<Text size="xs" fw="bold" style={{ display: "block" }}>
					{memberNames.length} Group Member
					{memberNames.length !== 1 ? "s" : ""}:
				</Text>
				<Text size="sm">
					{hasMemberNames ? (
						<>
							{displayNames.join(", ")}
							{remainingCount > 0 && (
								<span style={{ fontStyle: "italic" }}>
									... and {remainingCount} more
								</span>
							)}
						</>
					) : (
						"No members"
					)}
				</Text>
			</HoverCard.Dropdown>
		</HoverCard>
	);
};

class MatterPermissionsEditor extends React.Component {
	constructor(props) {
		super(props);

		// Create dictionaries from the user/group arrays
		const userDisplayNamesBySub = this.createUserDisplayNamesBySub(
			this.props.availableUsers
		);
		const availableGroupNamesByID = this.createAvailableGroupNamesByID(
			this.props.availableGroups
		);

		// Transform arrays to dropdown format once and store for reuse
		this.transformedAvailableUsers = this.props.availableUsers.map(
			(user) => ({
				...user,
				value: user.sub,
				label: user.displayName,
			})
		);

		this.transformedAvailableGroups = this.props.availableGroups.map(
			(g) => ({
				...g,
				value: g.id,
				label: g.name,
				isFixed: g.type === "FIRM_ADMIN",
			})
		);

		// Determine initial user/group allow lists based on incoming data structure
		const userAllowList = props.initialPermissions.userAllowList || [];
		const groupAllowList = props.initialPermissions.groupAllowList || [];

		this.state = {
			userAllowList: userAllowList
				.filter((u) => userDisplayNamesBySub[u.userSub])
				.map((u) => allowListDropdownMap(u, userDisplayNamesBySub)),
			groupAllowList: groupAllowList
				.filter((g) => availableGroupNamesByID[g.groupID])
				.map((g) => groupListDropdownMap(g, availableGroupNamesByID)),
			matterPermissionGlobalSetting:
				matterPermissionGlobalSettingOptions[
					matterPermissionGlobalSettingLookup[
						props.initialPermissions
							.matterPermissionGlobalSetting || "allUsers"
					]
				],
		};

		this.handleChange = this.handleChange.bind(this);
	}

	createUserDisplayNamesBySub(users) {
		const dict = {};
		if (users) {
			users.forEach((u) => {
				dict[u.sub] = u.displayName;
			});
		}
		return dict;
	}

	createAvailableGroupNamesByID(groups) {
		const dict = {};
		if (groups) {
			groups.forEach((g) => {
				dict[g.id] = {
					name: g.name,
					type: g.type,
					memberNames: g.memberNames,
				};
			});
		}
		return dict;
	}

	handleChange(key, value) {
		const changeCallback = (setToAllUsers) => {
			// Call the parent's onChange with normalized data
			this.props.onChange({
				matterPermissionGlobalSetting:
					this.state.matterPermissionGlobalSetting.value,
				userAllowList:
					this.state.userAllowList && !setToAllUsers
						? this.state.userAllowList.map((u) => ({
								userSub: u.value,
								type: "ALLOW_READ_WRITE",
							}))
						: [],
				groupAllowList:
					this.state.groupAllowList && !setToAllUsers
						? this.state.groupAllowList.map((g) => ({
								groupID: g.value,
								type: "ALLOW_READ_WRITE",
							}))
						: [],
			});
		};

		this.setState({ [key]: value }, () => {
			const setToAllUsers =
				this.state.matterPermissionGlobalSetting.value === "allUsers";

			// Auto-add admin groups if switching to specified users with no groups selected
			if (!setToAllUsers && this.state.groupAllowList.length === 0) {
				this.setState(
					{
						groupAllowList: findAdminGroups(
							this.transformedAvailableGroups
						),
					},
					() => changeCallback(setToAllUsers)
				);
			} else {
				changeCallback(setToAllUsers);
			}
		});
	}

	render() {
		const { labelType = "SINGLE_MATTER", disabled = false } = this.props;

		// Get label configuration based on labelType prop
		const labelConfig = LABEL_CONFIGS[labelType] || {};
		const labels = {
			mainLabel: labelConfig.main,
			userLabel: labelConfig.user,
			groupLabel: labelConfig.group,
		};

		// Calculate unique users across all selected groups for the collapsible section
		const uniqueMemberNames = new Set();
		if (this.state.groupAllowList && this.state.groupAllowList.length > 0) {
			this.state.groupAllowList.forEach((group) => {
				const memberNames = group.memberNames || [];
				memberNames.forEach((name) => uniqueMemberNames.add(name));
			});
		}
		const uniqueUserCount = uniqueMemberNames.size;

		return (
			<div>
				<div>
					<LabeledDropDownSelect
						label={labels.mainLabel}
						options={matterPermissionGlobalSettingOptions}
						isDisabled={disabled}
						defaultValue={this.state.matterPermissionGlobalSetting}
						name="matterPermissionGlobalSetting"
						handler={this.handleChange}
					/>
				</div>
				{this.state.matterPermissionGlobalSetting.value ===
				"specifiedUsers" ? (
					<div>
						<div>
							<UsersDropDownSelect
								label={labels.userLabel}
								isMulti
								name="userAllowList"
								isDisabled={disabled}
								isAsync
								defaultValue={this.state.userAllowList}
								options={this.transformedAvailableUsers}
								handler={this.handleChange}
							/>
						</div>
						<div>
							<LabeledDropDownSelect
								label={labels.groupLabel}
								isMulti
								name="groupAllowList"
								isDisabled={disabled}
								isAsync
								isClearable
								value={this.state.groupAllowList}
								defaultValue={this.state.groupAllowList}
								options={this.transformedAvailableGroups}
								handler={this.handleChange}
								components={{ Option: GroupOptionWithTooltip }}
							/>
						</div>
						{this.state.groupAllowList &&
						this.state.groupAllowList.length > 0 ? (
							<div style={{ marginTop: 16 }}>
								<Accordion style={{ backgroundColor: "white" }}>
									<Accordion.Item value="group-members">
										<Accordion.Control>
											Group selection grants access to{" "}
											{uniqueUserCount} user
											{uniqueUserCount !== 1 ? "s" : ""}
										</Accordion.Control>
										<Accordion.Panel>
											<div style={{ width: "100%" }}>
												{this.state.groupAllowList.map(
													(group) => {
														const memberNames =
															group.memberNames ||
															[];
														const hasMemberNames =
															memberNames.length >
															0;

														return (
															<div
																key={
																	group.value
																}
																style={{
																	marginBottom: 12,
																}}
															>
																<Text
																	size="sm"
																	fw="bold"
																>
																	{
																		group.label
																	}
																	<span
																		style={{
																			marginLeft: 8,
																			color: "#666",
																			fontSize:
																				"0.9em",
																			fontWeight:
																				"normal",
																		}}
																	>
																		(
																		{
																			memberNames.length
																		}{" "}
																		member
																		{memberNames.length !==
																		1
																			? "s"
																			: ""}
																		)
																	</span>
																</Text>
																<Text
																	size="sm"
																	style={{
																		color: "#666",
																		marginLeft: 16,
																	}}
																>
																	{hasMemberNames
																		? memberNames.join(
																				", "
																			)
																		: "No members"}
																</Text>
															</div>
														);
													}
												)}
											</div>
										</Accordion.Panel>
									</Accordion.Item>
								</Accordion>
							</div>
						) : null}
					</div>
				) : null}
			</div>
		);
	}
}

MatterPermissionsEditor.propTypes = {
	// Initial permission data
	initialPermissions: PropTypes.shape({
		matterPermissionGlobalSetting: PropTypes.string,
		userAllowList: PropTypes.array,
		groupAllowList: PropTypes.array,
	}).isRequired,

	// Callback when permissions change
	onChange: PropTypes.func.isRequired,

	// Display options
	labelType: PropTypes.oneOf([
		"SINGLE_MATTER",
		"MULTIPLE_MATTERS",
		"DEFAULT_MATTER_PERMISSIONS",
	]), // Controls which label set to use
	disabled: PropTypes.bool,

	// Data for dropdowns (arrays of users and groups)
	availableUsers: PropTypes.array.isRequired,
	availableGroups: PropTypes.array.isRequired,
};

export default MatterPermissionsEditor;
